<template>
  <CommonDocumentContent class="app-docs-content" :data-show="true" :route-path="routePath" :prefix-path="prefixPath">
    <CommonDocumentHeader :border="false">
      <CommonDocumentTitle>
        {{ documentConfig.title }}
        <CommonDocumentUsedName
          v-if="documentInfo.used_names?.length"
          class="relative top-0.5"
          :used-names="documentInfo.used_names"
        />
      </CommonDocumentTitle>
      <GfrTitle
        v-if="documentInfo.subtitle"
        :level="3"
        class="mt-1 text-m-p !font-bold text-light max-lg:text-xs max-lg:leading-[1.125rem]"
        :placement="$route.params.lang === 'ar' ? 'right' : 'left'"
      >
        {{ documentInfo.subtitle }}
      </GfrTitle>
      <CommonDocumentTagWrapper v-if="commonTags.common_tag.length" class="mt-3 max-lg:mt-2" :data="commonTags" />
    </CommonDocumentHeader>
    <CommonDocumentTagWrapper v-if="Object.keys(documentTags).length" class="mt-3 max-lg:mt-2" :data="documentTags" />
    <p v-if="documentInfo.brief" class="app-docs-brief">
      {{ documentInfo.brief }}
    </p>
    <CommonDocumentMarkdown
      :content="documentConfig.content"
      :prefix-path="prefixPath"
      :next="nextDoc"
      :levels="!$route.params.id.includes('enum') ? [1, 2, 3] : [1]"
      :pre="prevDoc"
    />
    <!-- <CommonBackToTop class="absolute bottom-11 left-1/2 z-10 -translate-x-1/2" /> -->
  </CommonDocumentContent>
  <ClientOnly>
    <aside v-if="$toc.length > 0" class="app-docs-nav">
      <div class="app-docs-nav-inner">
        <div class="app-docs-nav-index">{{ formatTransify('UGC_OFFICIAL_WEBSITE_INDEX') }}</div>
        <GfrScrollArea
          class="app-docs-nav-scroll-area"
          :initial-y="scrollAreaY"
          :dir="$route.params.lang === 'ar' ? 'rtl' : 'ltr'"
        >
          <div class="app-docs-nav-table">
            <div
              class="absolute ltr:left-0 rtl:right-0 top-0 h-6 w-0.5 bg-primary transition-transform duration-200"
              :style="{
                transform: `translateY(${activeSlug ? activeSlugPos : 0}px)`,
                height: `${activeSlugHeight}px`
              }"
            />
            <CommonDocumentTableIndex
              class="text-[#C8CCD3] max-lg:hidden"
              :data="$toc"
              :max-level="!$route.params.id.includes('enum') ? 3 : 1"
              :level="1"
              :active-slug="activeSlug"
            />
          </div>
        </GfrScrollArea>
      </div>
    </aside>
  </ClientOnly>
</template>
<script lang="ts" setup>
import { DocumentInfo } from '~/server/types'
defineProps({
  prefixPath: String
})
const { $toc, $flatToc } = useNuxtApp()
const store = useStore()
const { formatTransify } = store
const documentStore = useDocumentStore()
const { documentConfig, nextDoc, prevDoc, routePath } = storeToRefs(documentStore)
const documentInfo = computed(() => {
  return documentConfig.value.doc_info || {}
})
const documentTags = computed(() => {
  const tags: any = {}
  for (const key in documentInfo.value) {
    if (key.includes('tag') && key !== 'common_tag') {
      tags[key] = documentInfo.value[key as keyof DocumentInfo]
    }
  }
  return tags
})
const commonTags = computed(() => {
  return {
    common_tag: documentInfo.value.common_tag || []
  }
})

const activeSlug = ref('')
const activeSlugHeight = ref(24)
const activeSlugPos = ref(0)
const offset = ref(0)
const mounted = ref(false)
const scrollAreaY = ref(0)
const navList = computed(() => {
  return $flatToc || []
})
const throttleUpdate = throttle(updateActiveSlug, 100)
function handleActiveSlug() {
  throttleUpdate(offset.value)
}
function getLayoutHeaderHeight() {
  const elem = document.querySelector('.app-layout-header') as HTMLElement
  return elem?.offsetHeight || 0
}
function updateActiveSlug(offset = 0) {
  if (Device().isMobile) return
  const scrollTop = document.documentElement.scrollTop || document.body.scrollTop
  const index = navList.value.findIndex((item: any) => {
    const { slug } = item
    if (!slug?.trim()) {
      return false
    }
    try {
      const elem = document.querySelector(`.app-markdown-content #${slug}`)
      const offsetTop = (elem as HTMLElement)?.offsetTop
      return scrollTop < offsetTop - offset * 2 // 方便向前取一个
    } catch (e) {
      console.error(e)
      return false
    }
  })
  const currentIndex = index === -1 ? navList.value.length - 1 : index - 1
  activeSlug.value = navList.value[currentIndex]?.slug || ''

  // Update active slug height based on element
  const activeElem = document.querySelector(`.app-doc-nav-link[data-slug="${activeSlug.value}"]`) as HTMLElement
  if (activeElem) {
    activeSlugHeight.value = 16
    activeSlugPos.value = activeElem.offsetTop + 8
  } else {
    activeSlugHeight.value = 0
    activeSlugPos.value = 0
  }
}
watch(
  () => activeSlug.value,
  (value) => {
    const elem = document.querySelector(`.app-doc-nav-link[data-slug="${value}"]`)
    if (elem) {
      scrollAreaY.value = (elem as HTMLElement).offsetTop
    } else {
      scrollAreaY.value = 0
    }
  }
)
onMounted(async () => {
  mounted.value = true
  await nextTick()
  offset.value = getLayoutHeaderHeight()
  updateActiveSlug(offset.value)
  window.addEventListener('scroll', handleActiveSlug)
})
onUnmounted(() => {
  window.removeEventListener('scroll', handleActiveSlug)
})
</script>

<style lang="scss" scoped>
.app-docs-content {
  @apply relative flex-1 overflow-hidden px-10 pb-13 opacity-0 data-[show=true]:opacity-100;
  @apply max-lg:px-5 max-lg:pb-5 max-2xl:px-6;
}
.app-docs-brief {
  @apply mt-6 text-m-p text-light max-lg:text-xs max-lg:leading-[1.125rem] max-lg:mt-4;
}
.app-docs-nav {
  @apply relative 2xl:w-64 flex-shrink-0 max-xl:hidden max-2xl:w-50;
}
.app-docs-nav-inner {
  @apply sticky left-0 top-34 flex max-h-[calc(100vh-10rem)] flex-col pb-3;
}
.app-docs-nav-index {
  @apply mb-6 flex-shrink-0 text-m-h4 font-medium capitalize;
}
.app-docs-nav-scroll-area {
  @apply w-full ltr:pr-4 rtl:pl-4;
}
.app-docs-nav-table {
  @apply relative h-full;
  @apply before:absolute before:left-0 before:top-0 before:h-full before:w-0.5 before:bg-[#999]/25;
  @apply ltr:pl-1.5 rtl:pr-1.5 rtl:before:right-0 rtl:before:left-auto;
}
</style>
